import {injectIntl} from "react-intl";
import React, {useContext, useEffect, useRef, useState} from "react";
import {AutoRetrieverDeviceOverviewContext} from "./context/AutoRetrieverDeviceOverviewContext";
import {GlobalStateContext} from "../../global/context/GlobalStateContext";
import {ASCENDING, CONFIG_TYPE, SEARCH_ROW_COLOR} from "../../util/Constants";
import {
    chainWalk,
    getAssetRefreshInterval,
    getFormattedTime,
    isTimestampWithinHours,
    sortComparator
} from "../../util/Util";
import Loader from "@amzn/meridian/loader";
import Column from "@amzn/meridian/column";
import ConfigurationTabHeader from "../../commonui/ConfigurationTabHeader";
import PaginatedTable from "../../commonui/PaginatedTable";
import {DraftContext} from "../../draftSave/context/DraftSaveContext";
import {TableCell, TableRow} from "@amzn/meridian/table";
import {getLocalizedMessage} from "../../util/Localization";
import Input from "@amzn/meridian/input";
import Button from "@amzn/meridian/button";
import Icon from "@amzn/meridian/icon";
import chevronDownSmallTokens from "@amzn/meridian-tokens/base/icon/chevron-down-small"
import chevronRightSmallTokens from "@amzn/meridian-tokens/base/icon/chevron-right-small"
import Text from "@amzn/meridian/text";
import Row from "@amzn/meridian/row";
import Box from "@amzn/meridian/box";
import refreshTokens from "@amzn/meridian-tokens/base/icon/refresh"
import Tooltip from "@amzn/meridian/tooltip";

const AutoRetrieverDeviceConfiguration = ({intl}) => {

    //Add Context
    const {viewModel, dispatch} = useContext(AutoRetrieverDeviceOverviewContext);
    const {getDraftData, setDraftData} = useContext(DraftContext);
    const { showCustomAlert } = useContext(GlobalStateContext);

    const [selectedMac, setSelectedMac] = useState("");
    const [valueConfiguration, setValueConfiguration] = useState("");
    const [scannablesSearchInput, setScannablesSearchInput] = useState('');
    const [testConfigSortColumn, setTestConfigSortColumn] = useState("id");
    const [testConfigSortOrder, setTestConfigSortOrder] = useState(ASCENDING);
    const [expandedRows, setExpandedRows] = useState([]);

    const tableEndRef = useRef(null);

    const PAGE_CONFIGURATION_KEYS = [CONFIG_TYPE.AUTO_RETRIEVER_TEST_CONFIGURATION];

    useEffect(() => {
        getData()
        const interval = setInterval(() => {
            if (!document.hidden) {
                getData();
            }
        }, getAssetRefreshInterval());
        return () => clearInterval(interval);
    }, []);

    useEffect(() => {
        setDraftData({
            data: chainWalk(() => extractTestConfigForPick(viewModel.testConfig[selectedMac]), []),
            stateKey: CONFIG_TYPE.AUTO_RETRIEVER_TEST_CONFIGURATION,
            subKey: selectedMac,
        })
    }, [selectedMac]);

    const getData = () => {
        dispatch({type: 'LOAD_VIEW', data: {dispatcher: dispatch}});
    }

    const onClickStartTest = () => {
        dispatch({type: "START_MOCK_TEST", data: {
            dispatcher: dispatch,
            showCustomAlert: showCustomAlert,
            mac: selectedMac
        }})
    }

    const isValidTestConfig = () => {
        const config = viewModel.testConfig[selectedMac];
        return config && config.picklists && isTimestampWithinHours(config.timestamp, 8);
    }

    const extractTestConfigForPick = (testConfigForAR) => {
        const result = [];
        if (!isValidTestConfig()) {
            return result;
        }
        testConfigForAR.picklists.forEach((picklist) => {
            const allPickItemsForPicklist = [];
            Object.keys(picklist.pickItems).forEach((cluster) => {
                picklist.pickItems[cluster].forEach((item) => {
                    item.cluster = cluster;
                    allPickItemsForPicklist.push(item);
                });
            });
            result.push({
                id: picklist.id + "#" + picklist.groupId,
                pickStation: picklist.pickStation,
                pickItems: allPickItemsForPicklist
            })
        });
        return result;
    }


    const scrollToBottom = () => {
        tableEndRef.current.scrollIntoView({behavior: "smooth"})
    }

    const updateExpandedRows = row => {
        const newExpandedRows = expandedRows.includes(row)
            ? expandedRows.filter(expandedRow => expandedRow !== row)
            : [...expandedRows, row]

        setExpandedRows(newExpandedRows)
    }

    const getSortedDeviceList = () => {
        // Device List sorted based on device "name"
        const sortedDeviceList = Object.values(viewModel.autoRetrieverDeviceInformation);
        sortedDeviceList && sortedDeviceList.sort((a, b) => {
            if (a.name < b.name) {
                return -1
            } else if (b.name > a.name) {
                return 1
            }
            return 0
        })
        return sortedDeviceList
    }

    const searchDataForTestConfigTable = () => {
        let data = getDraftData({
            stateKey: CONFIG_TYPE.AUTO_RETRIEVER_TEST_CONFIGURATION,
            subKey: selectedMac
        });

        data = data.filter(item => getTestConfigKey(item.pickItems)
            .toLowerCase()
            .includes(scannablesSearchInput.toLowerCase()))

        data = data.sort((a, b)=>
            sortTestConfigColumns[testConfigSortColumn](a, b, testConfigSortOrder === ASCENDING)
        )

        return data;
    }

    const getTestConfigKey = (pickItems) => {
        return pickItems.map(item => item.id).join(';');
    }

    const sortTestConfigColumns = {
        id: (a, b, ascending) => {
            return ascending ? sortComparator(a.id, b.id) : -sortComparator(a.id, b.id);
        },
        pickItemsCount: (a, b, ascending) => {
            return ascending ? sortComparator(a.pickItems.length, b.pickItems.length) : -sortComparator(a.pickItems.length, b.pickItems.length);
        },
        pickStation: (a, b, ascending) => {
            return ascending ? sortComparator(a.pickStation, b.pickStation) : -sortComparator(a.pickStation, b.pickStation);
        }
    }

    const renderSearchRow = () => {
        return (
            <TableRow backgroundColor={SEARCH_ROW_COLOR}>
                <TableCell columnSpan={3}/>
                <TableCell>
                    <Input
                        className="input-search-class"
                        value={scannablesSearchInput}
                        onChange={setScannablesSearchInput}
                        type="text"
                        placeholder="Search"
                        width="100%"
                        size="small"/>
                </TableCell>
                <TableCell columnSpan={3}/>
            </TableRow>
        )
    }

    const renderTestConfigExpandableTableHeader = () => {
        return (
            <TableRow highlightOnHover={false}>
                <TableCell width={60}>
                    {/* Empty table cell to hold the expandable Icon button */}
                </TableCell>
                <TableCell sortColumn="id">
                    {getLocalizedMessage({intl, stringId: "table.index"})}
                </TableCell>
                <TableCell sortColumn="pickItemsCount">
                    {getLocalizedMessage({intl, stringId: "testconfig.items_count"})}
                </TableCell>
                <TableCell>
                    {getLocalizedMessage({intl, stringId: "testconfig.pick_item"})}
                </TableCell>
                <TableCell>
                    {getLocalizedMessage({intl, stringId: "testconfig.pick_item_type"})}
                </TableCell>
                <TableCell>
                    {getLocalizedMessage({intl, stringId: "testconfig.cluster"})}
                </TableCell>
                <TableCell sortColumn="pickStation">
                    {getLocalizedMessage({intl, stringId: "testconfig.pick_station"})}
                </TableCell>
            </TableRow>
        )
    }

    const renderTestConfigExpandableTableRow = (config, serialNumber) => {
        let rows = [];
        rows.push(<TableRow>
            <TableCell
                rowSpan={expandedRows.includes(config.id) ? config.pickItems.length : 1}
                alignmentVertical="top">
                <Button
                    type="icon"
                    size="small"
                    onClick={() => updateExpandedRows(config.id)}>
                    {expandedRows.includes(config.id) ? (
                        <Icon tokens={chevronDownSmallTokens}>Collapse rows</Icon>
                    ) : (
                        <Icon tokens={chevronRightSmallTokens}>Expand rows</Icon>
                    )}
                </Button>
            </TableCell>
            <TableCell
                rowSpan={expandedRows.includes(config.id) ? config.pickItems.length : 1}
                alignmentVertical="top">
                <Column spacingInset="200 none none">{config.id}</Column>
            </TableCell>
            <TableCell
                rowSpan={expandedRows.includes(config.id) ? config.pickItems.length : 1}
                alignmentVertical="top">
                <Column spacingInset="200 none none">{config.pickItems.length} items</Column>
            </TableCell>
            <TableCell>{config.pickItems[0].id}</TableCell>
            <TableCell>{config.pickItems[0].type}</TableCell>
            <TableCell>{config.pickItems[0].cluster}</TableCell>
            <TableCell>{config.pickStation}</TableCell>
        </TableRow>);
        if (expandedRows.includes(config.id)) {
            config.pickItems.slice(1).map(item => (
                rows.push(<TableRow>
                    <TableCell showCell={false}>{/* Empty table cell */}</TableCell>
                    <TableCell showCell={false}>{/* Empty table cell */}</TableCell>
                    <TableCell>{item.id}</TableCell>
                    <TableCell>{item.type}</TableCell>
                    <TableCell>{item.cluster}</TableCell>
                    <TableCell>{config.pickStation}</TableCell>
                </TableRow>)
            ))
        }
        return rows;
    }

    const getHeaderProps = () => {
        return {
            isViewOnly: true,
            selectedMac,
            setSelectedMac,
            valueConfiguration,
            setValueConfiguration,
            defaultSelection: "",
            scrollToBottom,
            sortedDeviceList: getSortedDeviceList(),
            configurationKeys: PAGE_CONFIGURATION_KEYS,
            refreshDeps: [viewModel.timestamp]
        }
    }

    if (viewModel.loadingOverview) {
        return (<Loader/>)
    } else {
        return (
            <Column>
                <ConfigurationTabHeader {...getHeaderProps()}/>
                {valueConfiguration === CONFIG_TYPE.AUTO_RETRIEVER_TEST_CONFIGURATION &&
                    selectedMac &&
                    <Column>
                        { isValidTestConfig() &&
                            <PaginatedTable
                            data={searchDataForTestConfigTable()}
                            renderHeader={renderTestConfigExpandableTableHeader}
                            renderSearchRow={renderSearchRow}
                            renderRow={renderTestConfigExpandableTableRow}
                            sortColumn={testConfigSortColumn}
                            sortDirection={testConfigSortOrder}
                            onSort={({sortColumn, sortDirection}) => {
                                setTestConfigSortColumn(sortColumn);
                                setTestConfigSortOrder(sortDirection);
                            }}/>
                        }
                        <Row width="100%" widths={["100", "fill", "100"]}>
                            <Tooltip position="end"
                                     title={getLocalizedMessage({intl, stringId: "tooltip.start_retrieval_test_desc"})}>
                                <Button onClick={onClickStartTest}
                                        size="medium" type="primary">
                                    <Icon tokens={refreshTokens}/>
                                    {getLocalizedMessage({intl, stringId: "testconfig.start"})}
                                </Button>
                            </Tooltip>
                            <Box></Box>
                            { isValidTestConfig() &&
                                <Text type="b200">
                                    {getLocalizedMessage({intl, stringId: "common.data_created"})}
                                    : {getFormattedTime(intl.locale, viewModel.testConfig[selectedMac].timestamp)}
                                </Text>
                            }
                        </Row>
                        <div ref={tableEndRef}/>
                    </Column>
                }
            </Column>
        )
    }

}

export default injectIntl(AutoRetrieverDeviceConfiguration);


// WEBPACK FOOTER //
// ./src/components/devicemanagement/autoretriever/AutoRetrieverDeviceConfiguration.js